<script setup>
// ENVIAR UM MODELO APROVADO AO CLIENTE, DE DENTRO DA CONVERSA (25/09/2026).
//
// Duas portas abrem este seletor: o ícone do WhatsApp na barra do campo de
// resposta (ideia do Victor: "ao lado do emoji, das respostas rápidas e do
// arquivo") e o botão "Enviar modelo" do cartão da janela fechada.
//
// À esquerda a escolha e as variáveis; à direita o MESMO celular do editor de
// modelos, com o que o cliente vai ler. A primeira variável já vem com o
// primeiro nome do cliente — é o "Olá, {{1}}" de quase todo modelo.
// O envio é do servidor (Advup::Wame::EnvioDeModelo): a mensagem entra na
// conversa pelo tempo real, como qualquer outra.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import PreviaDoCelular from 'dashboard/routes/dashboard/settings/whatsappTemplates/PreviaDoCelular.vue';
import {
  corpoDe,
  doModeloDaMeta,
  formatarComoWhatsapp,
  nomeLegivel,
  variaveisDe,
} from 'dashboard/helper/modeloWhatsapp';
import { nomeCurto } from 'dashboard/helper/avatar';

const props = defineProps({
  conversa: { type: Object, required: true },
});
const emit = defineEmits(['close', 'enviado']);

const { t } = useI18n();
const E = 'WHATSAPP_TEMPLATES.MODELOS.ENVIAR';
const M = 'WHATSAPP_TEMPLATES.MODELOS';

const caixaId = computed(() => props.conversa.inbox_id);
const nomeDoCliente = computed(() => props.conversa.meta?.sender?.name || '');

const carregando = ref(true);
const falhou = ref(false);
const aprovados = ref([]);
const emAnalise = ref(0);
const perfil = ref({});
const busca = ref('');
const escolhido = ref(null);
const valores = ref([]);
const valorDoCabecalho = ref('');
const enviando = ref(false);
const erro = ref('');

const tituloDe = x => x.titulo || nomeLegivel(x.name);
const visiveis = computed(() => {
  const termo = busca.value.trim().toLowerCase();
  if (!termo) return aprovados.value;
  return aprovados.value.filter(
    x =>
      tituloDe(x).toLowerCase().includes(termo) ||
      corpoDe(x).toLowerCase().includes(termo)
  );
});

async function carregar() {
  carregando.value = true;
  falhou.value = false;
  try {
    const { data } = await WhatsappTemplatesAPI.list(caixaId.value);
    const todos = data?.data || [];
    aprovados.value = todos.filter(x => x.status === 'APPROVED');
    emAnalise.value = todos.filter(x =>
      ['PENDING', 'IN_APPEAL'].includes(x.status)
    ).length;
  } catch {
    falhou.value = true;
  } finally {
    carregando.value = false;
  }
  WhatsappTemplatesAPI.numero(caixaId.value)
    .then(({ data }) => {
      perfil.value = data || {};
    })
    .catch(() => {});
}
onMounted(carregar);

// ── O modelo escolhido ──────────────────────────────────────────────────────
const variaveisDoCorpo = computed(() =>
  escolhido.value
    ? [...variaveisDe(corpoDe(escolhido.value))].sort((a, b) => a - b)
    : []
);
const cabecalhoTemVariavel = computed(() => {
  const cab = (escolhido.value?.components || []).find(
    c => (c.type || '').toUpperCase() === 'HEADER'
  );
  return (
    (cab?.format || '').toUpperCase() === 'TEXT' &&
    variaveisDe(cab.text).length > 0
  );
});

function escolher(x) {
  escolhido.value = x;
  erro.value = '';
  const n = variaveisDe(corpoDe(x)).length;
  valores.value = Array.from({ length: n }, (_, i) =>
    i === 0 ? nomeCurto(nomeDoCliente.value) : ''
  );
  valorDoCabecalho.value = '';
}

// A prévia é o editor de modelos lendo o modelo da Meta, com os VALORES deste
// envio no lugar dos exemplos.
const previa = computed(() => {
  if (!escolhido.value) return null;
  const m = doModeloDaMeta(escolhido.value);
  m.exemplos = [...valores.value];
  m.cabecalho.exemplo = valorDoCabecalho.value;
  return m;
});

const faltaAlgo = computed(
  () =>
    valores.value.some(v => !(v || '').trim()) ||
    (cabecalhoTemVariavel.value && !valorDoCabecalho.value.trim())
);

const PRECO = { UTILITY: 'UTILITY_PRECO', MARKETING: 'MARKETING_PRECO' };
const precoDe = x =>
  PRECO[x.category] ? t(`${M}.IDENT.${PRECO[x.category]}`) : '';

async function enviar() {
  if (!escolhido.value || faltaAlgo.value) return;
  enviando.value = true;
  erro.value = '';
  try {
    await WhatsappTemplatesAPI.enviarNaConversa(props.conversa.id, {
      nome: escolhido.value.name,
      idioma: escolhido.value.language,
      parametros: valores.value.map(v => v.trim()),
      parametro_do_cabecalho: valorDoCabecalho.value.trim() || undefined,
    });
    useAlert(t(`${E}.ENVIADO`));
    emit('enviado');
    emit('close');
  } catch (e) {
    erro.value = e?.response?.data?.error || t(`${E}.ERRO`);
  } finally {
    enviando.value = false;
  }
}

// "{{1}}" dentro de {{ }} no template quebraria a interpolação do Vue.
const rotuloVar = n => `{{${n}}}`;
const linhaDe = x =>
  formatarComoWhatsapp(
    corpoDe(x).replace(/\n+/g, ' '),
    (x.components || []).find(c => (c.type || '').toUpperCase() === 'BODY')
      ?.example?.body_text?.[0] || []
  );
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div
      v-modal-acessivel="() => emit('close')"
      class="advup-modal advup-modal--claro advup-modal--largo advup-modal--teto env-mdl"
    >
      <div class="advup-modal__topo advup-modal__topo--teal">
        <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
             degradê escuro (05/10/2026). -->
        <i
          class="advup-modal__marca i-ph-whatsapp-logo-fill"
          aria-hidden="true"
        />
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-whatsapp-logo-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">{{ t(`${E}.TITULO`) }}</h3>
          <p class="advup-modal__sub">
            {{ t(`${E}.SUB`, { nome: nomeDoCliente || t(`${E}.CLIENTE`) }) }}
          </p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t(`${E}.FECHAR`)"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <div class="advup-modal__corpo env-mdl__corpo">
        <div class="env-mdl__esquerda">
          <!-- 1. Qual modelo -->
          <template v-if="!escolhido">
            <div class="advup-busca advup-busca--bloco">
              <i
                class="i-ph-magnifying-glass advup-busca__lupa"
                aria-hidden="true"
              />
              <input
                v-model="busca"
                type="text"
                class="advup-busca__campo reset-base"
                :aria-label="t(`${E}.BUSCA`)"
                :placeholder="t(`${E}.BUSCA`)"
              />
            </div>

            <p v-if="carregando" class="advup-nota">
              {{ t(`${E}.CARREGANDO`) }}
            </p>
            <EstadoVazio
              v-else-if="falhou"
              tom="erro"
              icone="i-ph-warning-circle"
              :titulo="t(`${E}.ERRO_CARGA`)"
            >
              <button type="button" class="advup-btn" @click="carregar">
                {{ t(`${M}.LISTA.TENTAR`) }}
              </button>
            </EstadoVazio>
            <EstadoVazio
              v-else-if="!aprovados.length"
              icone="i-ph-chat-centered-text"
              :titulo="t(`${E}.NENHUM_TIT`)"
              :dica="
                emAnalise
                  ? t(`${E}.NENHUM_EM_ANALISE`, { n: emAnalise }, emAnalise)
                  : t(`${E}.NENHUM_DICA`)
              "
            >
              <router-link
                :to="{
                  name: 'whatsapp_templates_list',
                  query: { caixa: caixaId },
                }"
                class="advup-btn"
              >
                {{ t(`${E}.IR_PARA_MODELOS`) }}
              </router-link>
            </EstadoVazio>
            <ul v-else class="env-mdl__lista">
              <li v-for="(x, i) in visiveis" :key="x.id || x.name">
                <button
                  type="button"
                  class="env-mdl__item"
                  :style="{ '--i': i }"
                  @click="escolher(x)"
                >
                  <span class="env-mdl__item-cab">
                    <span class="env-mdl__item-tit">{{ tituloDe(x) }}</span>
                    <span class="env-mdl__preco">{{ precoDe(x) }}</span>
                  </span>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span class="env-mdl__item-txt" v-html="linhaDe(x)" />
                </button>
              </li>
            </ul>
          </template>

          <!-- 2. As variáveis -->
          <template v-else>
            <button
              type="button"
              class="env-mdl__voltar"
              @click="escolhido = null"
            >
              <i class="i-hugeicons-arrow-left-01" aria-hidden="true" />
              {{ t(`${E}.TROCAR`) }}
            </button>
            <p class="env-mdl__escolhido">{{ tituloDe(escolhido) }}</p>

            <div
              v-if="cabecalhoTemVariavel || variaveisDoCorpo.length"
              class="env-mdl__vars"
            >
              <span class="advup-rotulo">{{ t(`${E}.PREENCHA`) }}</span>
              <label v-if="cabecalhoTemVariavel" class="env-mdl__var">
                <span class="env-mdl__var-rot">{{ t(`${E}.TITULO_VAR`) }}</span>
                <input
                  v-model="valorDoCabecalho"
                  type="text"
                  class="advup-campo"
                />
              </label>
              <label
                v-for="(n, i) in variaveisDoCorpo"
                :key="n"
                class="env-mdl__var"
              >
                <span class="env-mdl__var-rot">{{ rotuloVar(n) }}</span>
                <input
                  v-model="valores[i]"
                  type="text"
                  class="advup-campo"
                  :aria-label="rotuloVar(n)"
                />
              </label>
              <p class="advup-nota">{{ t(`${E}.DICA_VAR`) }}</p>
            </div>
            <p v-else class="advup-nota">{{ t(`${E}.SEM_VAR`) }}</p>

            <p class="advup-nota env-mdl__custo">
              <i class="i-ph-receipt" aria-hidden="true" />
              {{ t(`${E}.CUSTO`, { preco: precoDe(escolhido) }) }}
            </p>

            <p v-if="erro" class="env-mdl__erro" role="alert">
              <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
              <span>{{ erro }}</span>
            </p>
          </template>
        </div>

        <div class="env-mdl__direita">
          <PreviaDoCelular v-if="previa" :modelo="previa" :perfil="perfil" />
          <div v-else class="env-mdl__sem-previa">
            <i class="i-ph-device-mobile" aria-hidden="true" />
            <p class="advup-nota">{{ t(`${E}.ESCOLHA_PARA_VER`) }}</p>
          </div>
        </div>
      </div>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t(`${E}.CANCELAR`) }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="!escolhido || faltaAlgo || enviando"
          @click="enviar"
        >
          <i
            :class="
              enviando
                ? 'i-ph-circle-notch advup-gira'
                : 'i-ph-paper-plane-tilt-fill'
            "
            aria-hidden="true"
          />
          {{ enviando ? t(`${E}.ENVIANDO`) : t(`${E}.ENVIAR`) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.env-mdl__corpo {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: var(--esp-6);
  align-items: start;
}

.env-mdl__esquerda {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  min-width: 0;
}

.env-mdl__direita {
  position: sticky;
  top: 0;
}

.env-mdl__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.env-mdl__item {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  width: 100%;
  padding: var(--esp-3) var(--esp-4);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  text-align: left;
  cursor: pointer;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background var(--mov-1) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
  animation: env-mdl-sobe var(--mov-2) var(--mov-curva) backwards;
  animation-delay: calc(var(--i, 0) * var(--cascata-passo));
}

.env-mdl__item:hover {
  border-color: rgb(var(--teal-7));
  background: rgb(var(--teal-2));
  transform: translateY(-1px);
}

.env-mdl__item:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.env-mdl__item-cab {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--esp-2);
}

.env-mdl__item-tit {
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.env-mdl__preco {
  flex-shrink: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-10));
}

.env-mdl__item-txt {
  display: -webkit-box;
  overflow: hidden;
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.env-mdl__item-txt :deep(.mdl-var) {
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
}

.env-mdl__voltar {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  align-self: flex-start;
  padding: var(--esp-1) 0;
  border: 0;
  background: transparent;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--blue-11));
  cursor: pointer;
}

.env-mdl__voltar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.env-mdl__escolhido {
  margin: 0;
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}

.env-mdl__vars {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: var(--esp-4);
  border-radius: var(--r-3);
  background: rgb(var(--teal-2));
}

.env-mdl__var {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}

.env-mdl__var-rot {
  flex-shrink: 0;
  min-width: 52px;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-2);
  background: rgb(var(--teal-4));
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--teal-12));
  text-align: center;
}

.env-mdl__custo {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: 0;
}

.env-mdl__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-12));
}

.env-mdl__erro > i {
  flex-shrink: 0;
  margin-top: 0.2em;
  color: rgb(var(--ruby-9));
}

.env-mdl__sem-previa {
  display: grid;
  place-items: center;
  gap: var(--esp-2);
  min-height: 320px;
  padding: var(--esp-5);
  border: 1px dashed rgb(var(--slate-6));
  border-radius: var(--r-5);
  color: rgb(var(--slate-10));
  text-align: center;
}

.env-mdl__sem-previa > i {
  font-size: var(--ico-4);
}

@keyframes env-mdl-sobe {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

/* Celular: a prévia desce para baixo da escolha. */
@media (max-width: 767px) {
  .env-mdl__corpo {
    grid-template-columns: minmax(0, 1fr);
  }

  .env-mdl__direita {
    position: static;
  }

  .env-mdl__sem-previa {
    display: none;
  }
}
</style>
